import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { StepContact } from "@/components/intake/StepContact";
import { StepProduct } from "@/components/intake/StepProduct";
import { StepQuery } from "@/components/intake/StepQuery";
import { StepComment } from "@/components/intake/StepComment";
import { ThankYou } from "@/components/intake/ThankYou";
import { StatusLookup } from "@/components/intake/StatusLookup";

const STEPS = ["Contact", "Product", "Your query", "Comment"];
const EMPTY = { name: "", phone: "", email: "", product: "", batch_mode: "", batch: "", query: "", addons: [], offline_interest: false, comment: "" };

export default function PublicForm() {
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState("form");

  useEffect(() => {
    api.get("/form-config").then((r) => setConfig(r.data)).catch(() => toast.error("Could not load the form. Please refresh."));
  }, []);

  const update = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setErrors({});
  };

  const validate = () => {
    const e = {};
    if (step === 0) {
      if (form.name.trim().length < 2) e.name = "Please enter your full name";
      if (!/^\+?[\d\s-]{7,20}$/.test(form.phone.trim())) e.phone = "Enter a valid WhatsApp number";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Enter a valid email address";
    }
    if (step === 1 && !form.product) e.product = "Please choose what your query is about";
    if (step === 2) {
      if (form.product === config.challenge_product) {
        if (!form.batch_mode) e.batch_mode = "Please tell us about your batch";
        if (form.batch_mode === "known" && !form.batch.trim()) e.batch = "Enter your batch code, e.g. C10-B1";
      }
      if (!form.query) e.query = "Please choose your query";
      if (form.product === config.addon_product && form.query === config.addon_query && form.addons.length === 0)
        e.addons = "Tick at least one add-on";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => validate() && setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        product: form.product,
        query: form.query,
        addons: form.product === config.addon_product && form.query === config.addon_query ? form.addons : [],
        batch: form.product === config.challenge_product ? (form.batch_mode === "known" ? form.batch.trim() : config.batch_unknown) : null,
        offline_interest: form.offline_interest,
        comment: form.comment.trim(),
      };
      const { data } = await api.post("/tickets", payload);
      setResult(data);
      toast.success(`Ticket ${data.ticket_id} created`);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm(EMPTY);
    setStep(0);
    setResult(null);
  };

  return (
    <div className="min-h-screen bg-[#FFFBEB] grain">
      <header className="border-b-2 border-black bg-[#FACC15]">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4 sm:px-6">
          <Brand subtitle="Support Desk" />
          <div className="flex items-center gap-4 text-sm font-bold">
            <button
              type="button"
              onClick={() => setMode(mode === "form" ? "lookup" : "form")}
              className="rounded-full border-2 border-black bg-white px-3 py-1.5 transition-colors hover:bg-[#FEF9C3]"
              data-testid="toggle-mode-button"
            >
              {mode === "form" ? "Check ticket status" : "Raise a query"}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-12">
        {mode === "lookup" ? (
          <StatusLookup />
        ) : result ? (
          <ThankYou result={result} onReset={reset} />
        ) : (
          <>
            <div className="mb-8 fade-up">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-600">Student help centre</p>
              <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">How can we help you today?</h1>
              <p className="mt-3 text-base text-zinc-700 sm:text-lg">Answer a few quick questions and we'll raise a ticket for you. It takes under a minute.</p>
            </div>

            <ol className="mb-6 grid grid-cols-4 gap-2" data-testid="form-stepper">
              {STEPS.map((label, i) => (
                <li key={label} className="flex flex-col gap-1.5">
                  <span className={`h-2 rounded-full border border-black ${i <= step ? "bg-black" : "bg-white"}`} />
                  <span className={`text-[11px] font-bold uppercase tracking-wider sm:text-xs ${i === step ? "text-black" : "text-zinc-500"}`}>
                    {i + 1}. {label}
                  </span>
                </li>
              ))}
            </ol>

            <section className="brut-card brut-shadow p-5 sm:p-8">
              {!config ? (
                <p className="text-zinc-600" data-testid="form-loading">Loading form…</p>
              ) : (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.22 }}
                  >
                    {step === 0 && <StepContact form={form} update={update} errors={errors} />}
                    {step === 1 && <StepProduct form={form} update={update} errors={errors} config={config} />}
                    {step === 2 && <StepQuery form={form} update={update} errors={errors} config={config} />}
                    {step === 3 && <StepComment form={form} update={update} config={config} />}
                  </motion.div>
                </AnimatePresence>
              )}

              {config && (
                <div className="mt-8 flex flex-col-reverse gap-3 border-t-2 border-dashed border-zinc-300 pt-6 sm:flex-row sm:justify-between">
                  <button type="button" onClick={back} disabled={step === 0} className="btn-ghost" data-testid="form-back-button">
                    Back
                  </button>
                  {step < STEPS.length - 1 ? (
                    <button type="button" onClick={next} className="btn-primary" data-testid="form-next-button">
                      Continue
                    </button>
                  ) : (
                    <button type="button" onClick={submit} disabled={submitting} className="btn-primary" data-testid="public-form-submit-button">
                      {submitting ? "Submitting…" : "Submit my query"}
                    </button>
                  )}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
